/**
 * TypeScript interfaces for self-referral workflow
 *
 * These interfaces define the complete data structures used throughout
 * the self-referral process, from form submission to dietitian selection
 * and final referral creation.
 */

import type { PaymentGuidance } from '@/types/paymentGuidance';

/**
 * Version of the collection notice shown under the self-referral form
 * (design-care-coordinator-referral-tracking §4.6, D10/D12; final wording is OQ2).
 * Bump when the notice text changes; the backend stores it on the session row.
 */
export const COLLECTION_NOTICE_VERSION = '2026-10-v1';

/**
 * Form data collected from patients during self-referral process
 */
export interface SelfReferralFormData {
  /** Patient's first name */
  first_name: string;
  /** Patient's last name */
  last_name: string;
  /** Patient's email address (required for communication) */
  email: string;
  /** Optional phone number for contact with country code */
  phone?: {
    countryCode: string;
    isoCode: string;
    number: string;
  };
  /** Optional date of birth in YYYY-MM-DD format */
  date_of_birth?: string;
  /** Array of health condition strings selected by patient */
  conditions: string[];
  /** Optional additional notes or comments from patient */
  additional_notes?: string;
}

/**
 * Dietitian information returned from matching algorithm
 */
export interface MatchedDietitian {
  /** Unique identifier for the dietitian */
  dietician_id: string;
  /** Dietitian's display name */
  name: string;
  /** Overall matching score (0-100) */
  score: number;
  /** Score based on specialty matching (0-100) */
  specialty_score: number;
  /** Score based on current workload balance (0-100) */
  load_balance_score: number;
  /** Array of dietitian's specialisation areas */
  specializations: string[];
  /** Array of reasons why this dietitian was matched */
  match_reasons: string[];
  /** Server-resolved fee guidance for this dietitian (design §4.5, D4) */
  payment_guidance?: PaymentGuidance | null;
}

/**
 * Request payload for creating a direct session
 */
export interface DirectSessionRequest {
  dietitian_id: string;
  provider_id: string;
  patient_data: {
    patient_name: string;
    email: string;
    phone?: string;
    date_of_birth?: string;
    conditions: string[];
    additional_notes?: string;
    collection_notice_version?: string;
  };
}

/**
 * Response from session creation API endpoint
 * Contains matched dietitians and session information
 */
export interface SessionResponse {
  /** Unique session token for this referral session */
  session_token: string;
  /** Array of top matched dietitians (typically 3) */
  matched_dieticians: MatchedDietitian[];
  /** ISO timestamp when session expires */
  expires_at: string;
}

/**
 * Response from dietitian selection and referral finalisation
 */
export interface FinalizedReferralResponse {
  /** Token for the created referral */
  referral_token: string;
  /** Unique identifier for the referral record */
  referral_id: string;
  /** The selected dietitian information */
  selected_dietitian: MatchedDietitian;
  /** Complete patient data as stored in the system */
  patient_data: Record<string, unknown>;
  /** Pre-filled data for registration form */
  registration_prefill_data: {
    /** Patient's name */
    patient_name: string;
    /** Patient's email address */
    email: string;
    /** Optional phone number */
    phone?: string;
    /** Optional date of birth */
    date_of_birth?: string;
  };
}

/**
 * Workflow step constants for tracking progress
 */
export const WorkflowStep = {
  FORM: 'form',
  SELECTION: 'selection',
  CONFIRMATION: 'confirmation',
  COMPLETED: 'completed'
} as const;

export type WorkflowStep = typeof WorkflowStep[keyof typeof WorkflowStep];

/**
 * Current state of the self-referral workflow
 */
export interface WorkflowState {
  /** Current step in the workflow */
  currentStep: WorkflowStep;
  /** Form data collected from patient */
  formData?: SelfReferralFormData;
  /** Session information from API */
  sessionData?: SessionResponse;
  /** Selected dietitian information */
  selectedDietitian?: MatchedDietitian;
  /** Final referral response */
  finalReferral?: FinalizedReferralResponse;
  /** Any error messages */
  error?: string;
  /** Loading state indicators */
  isLoading: boolean;
}

/**
 * Session timer configuration and state
 */
export interface SessionTimerState {
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Whether the session has expired */
  isExpired: boolean;
  /** Whether the timer is actively counting down */
  isActive: boolean;
}

/**
 * Storage context metadata for referral data
 */
export interface ReferralStorageContext {
  /** Timestamp when data was stored */
  timestamp: string;
  /** Provider ID associated with this referral */
  providerId?: string;
  /** Session token for validation */
  sessionToken?: string;
  /** Expiration timestamp */
  expiresAt?: string;
}

/**
 * Complete stored referral data structure
 */
export interface StoredReferralData {
  /** Workflow state information */
  workflow: WorkflowState;
  /** Storage context metadata */
  context: ReferralStorageContext;
}

/**
 * API error response structure
 */
export interface SelfReferralApiError {
  /** HTTP status code */
  status: number;
  /** Error message */
  message: string;
  /** Detailed error information */
  detail?: string;
  /** Field-specific validation errors */
  errors?: Record<string, string[]>;
}

/**
 * Form validation error structure
 */
export interface ValidationErrors {
  /** Field name to error messages mapping */
  [fieldName: string]: string[];
}

/**
 * Form validation result
 */
export interface ValidationResult {
  /** Whether the form data is valid */
  isValid: boolean;
  /** Validation errors if any */
  errors: ValidationErrors;
}

/**
 * Health condition option for form selection
 */
export interface HealthConditionOption {
  /** Unique identifier for the condition */
  id: string;
  /** Display label for the condition */
  label: string;
  /** Optional description or additional information */
  description?: string;
  /** Whether this condition is commonly selected */
  isCommon?: boolean;
}

/**
 * Provider information for self-referral context
 */
export interface ProviderInfo {
  /** Unique provider identifier */
  provider_id: string;
  /** Provider's display name */
  name: string;
  /** Provider's practice or organisation name */
  practice_name?: string;
  /** Provider's specialty or type */
  specialty?: string;
}

/**
 * Self-referral landing page props
 */
export interface SelfReferralLandingPageProps {
  /** Provider ID from URL parameters */
  providerId: string;
}

/**
 * Self-referral form component props
 */
export interface SelfReferralFormProps {
  /** Callback when form is submitted */
  onSubmit: (data: SelfReferralFormData) => void;
  /** Whether form is currently submitting */
  isLoading: boolean;
  /** Error message to display */
  error?: string;
  /** Initial form data for pre-filling */
  initialData?: Partial<SelfReferralFormData>;
  /** Label for the submit button */
  submitLabel?: string;
}

/**
 * Dietitian selection step component props
 */
export interface DietitianSelectionStepProps {
  /** Array of matched dietitians to display */
  dietitians: MatchedDietitian[];
  /** Callback when dietitian is selected */
  onSelect: (dietitian: MatchedDietitian) => void;
  /** Whether selection is currently processing */
  isLoading: boolean;
  /** Error message to display */
  error?: string;
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when next button is clicked */
  onNext?: () => void;
  /** Whether a dietitian has been selected */
  hasSelection?: boolean;
}

/**
 * Session timer component props
 */
export interface SessionTimerProps {
  /** Session expiration timestamp */
  expiresAt: string;
  /** Callback when session expires */
  onExpire: () => void;
  /** Optional callback for time updates */
  onTimeUpdate?: (timeRemaining: number) => void;
}

/**
 * Hook return type for self-referral workflow management
 */
export interface UseSelfReferralWorkflowReturn {
  /** Current workflow state */
  workflowState: WorkflowState;
  /** Form data setter */
  setFormData: (data: SelfReferralFormData) => void;
  /**
   * Matched dietitians from session.
   * `undefined` = not yet fetched (loading); `[]` = fetched, empty
   * (design-dietitian-accreditation §4.8 — the wizard renders the empty state, not loading).
   */
  matches?: MatchedDietitian[];
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Submit form and create session */
  submitForm: (data: SelfReferralFormData) => Promise<void>;
  /** Select dietitian and finalise referral */
  selectDietitian: (dietitian: MatchedDietitian) => Promise<void>;
  /** Current session token */
  sessionToken: string | null;
  /** Time remaining in session */
  timeRemaining: number;
  /** Reset workflow to initial state */
  resetWorkflow: () => void;
  /** Handle session expiration */
  handleSessionExpire: () => void;
}

/**
 * Hook return type for session timer management
 */
export interface UseSessionTimerReturn {
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Whether session has expired */
  isExpired: boolean;
  /** Whether timer is active */
  isActive: boolean;
  /** Start the timer */
  startTimer: (expiresAt: string) => void;
  /** Stop the timer */
  stopTimer: () => void;
  /** Reset the timer */
  resetTimer: () => void;
}

/**
 * Hook return type for referral detection in registration
 */
export interface UseReferralDetectionReturn {
  /** Whether a referral token was detected */
  hasReferral: boolean;
  /** The referral token if present */
  referralToken: string | null;
  /** Pre-fill data from referral */
  prefillData: FinalizedReferralResponse['registration_prefill_data'] | null;
  /** Loading state while fetching referral data */
  isLoading: boolean;
  /** Error message if referral data fetch failed */
  error: string | null;
  /** Clear referral data after successful registration */
  clearReferral: () => void;
}

/**
 * Storage operation result
 */
export interface StorageOperationResult {
  /** Whether the operation was successful */
  success: boolean;
  /** Error message if operation failed */
  error?: string;
}

/**
 * Browser storage capabilities detection
 */
export interface StorageCapabilities {
  /** Whether sessionStorage is available */
  hasSessionStorage: boolean;
  /** Whether localStorage is available */
  hasLocalStorage: boolean;
  /** Whether storage is functional (not disabled/full) */
  isStorageFunctional: boolean;
}